import React, { useEffect, useRef, useState } from 'react';

export default function PillSelect({ label, items = [], value, onChange, maxWidth = 360 }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, width: Math.min(maxWidth, 360) });

  const clamp = (x, min, max) => Math.max(min, Math.min(max, x));

  const computePos = () => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const gutter = 8;
    const width = Math.min(maxWidth, vw - gutter * 2);
    const center = r.left + r.width / 2;
    const left = clamp(center - width / 2, gutter, vw - width - gutter);
    const top = r.bottom + 10;
    setPos({ left, top, width });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const onReflow = () => computePos();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, { passive: true });
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow);
    };
  }, [open, maxWidth]);

  const toggle = () => setOpen((o) => !o);
  const close = () => setOpen(false);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); }
  };

  return (
    <div className="pillWrap" ref={wrapRef}>
      <button
        type="button"
        className={`pill ${open ? 'pill--open' : ''}`}
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="pillLabel">{label}</span>
        <span className="pillCaret" aria-hidden>▾</span>
      </button>

      {open && (
        <>
          <div className="pillBackdrop" onClick={close} />
          <div
            className="pillPanel"
            role="listbox"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              width: pos.width,
              maxWidth: pos.width,
              zIndex: 1001,
            }}
          >
            <div className="pillPanelHeader">
              <div className="pillPanelTitle">{label}</div>
              <div className="pillPanelHint">Choose an option</div>
            </div>

            <div className="pillList">
              {items.map((opt) => {
                const checked = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className={`pillItem ${checked ? 'pillItem--active' : ''}`}
                    onClick={() => { onChange(opt.value); close(); }}
                  >
                    <span className={`pillCheck ${checked ? 'pillCheck--on' : ''}`} aria-hidden>
                      <span className="pillCheckDot" />
                    </span>
                    <span className="pillItemText">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
